import { useCallback, useEffect, useRef, type MouseEvent, type ReactNode } from "react";
import { motionIsReduced } from "@/hooks/useReducedMotionPref";

/**
 * Where the glow rests when the cursor is elsewhere: 85% across the band, on
 * its top edge — exactly where the hero's static highlight has always sat.
 * Tracking is expressed as a *delta* from this anchor, so "not tracking" is
 * literally `translate3d(0, 0, 0)` and a device that never fires a mousemove
 * keeps the original look with no extra branch.
 */
const GLOW_ANCHOR_X = 0.85;

/**
 * Fraction of the remaining distance the glow covers each frame — the whole
 * feel of the effect.
 *
 * Deliberately lazy. This is light diffusing through the band, not a pointer,
 * so it should read as a drift the cursor leads rather than a follow that
 * keeps up: at 0.065 the blob closes half the gap in ~10 frames and takes
 * something like a second and a half to run a full band-width chase down,
 * which is slow enough that the eye reads a continuous glide instead of
 * per-frame steps. Raising this tightens the leash and the fluidity goes with
 * it — the first pass at 0.15 already felt like a follow.
 */
const GLOW_EASE = 0.065;

/**
 * Sub-pixel deltas are invisible, so the chase treats them as arrival: it
 * snaps the last fraction and stops until the next mousemove.
 */
const GLOW_SETTLE_PX = 0.5;

/**
 * The idle drift's pace — far lazier than the chase. This is ambient light
 * wandering the band, not something following anyone; at this ease a waypoint
 * several hundred px away takes tens of seconds to reach, which is the point:
 * you notice the glow has moved, never that it is moving.
 */
const WANDER_EASE = 0.012;

/** Close enough to a waypoint that the drift picks its next one. */
const WANDER_ARRIVE_PX = 8;

/**
 * The drift is a bounded random walk FROM WHEREVER THE GLOW IS, not a tour of
 * fixed stations: each leg is a short random step from the current position,
 * clamped to the band. That's what lets a cursor hand-off feel continuous —
 * the glow starts strolling from where the mouse left it instead of gliding
 * clear across the band to some previous haunt.
 */
const WANDER_STEP = { x: 0.2, y: 0.35 };
const WANDER_BOUNDS = { xMin: -0.8, xMax: 0.1, yMin: -0.05, yMax: 0.85 };

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

const wanderWaypoint = (from: { x: number; y: number }, width: number, height: number) => ({
  x: clamp(
    from.x + (Math.random() * 2 - 1) * WANDER_STEP.x * width,
    WANDER_BOUNDS.xMin * width,
    WANDER_BOUNDS.xMax * width,
  ),
  y: clamp(
    from.y + (Math.random() * 2 - 1) * WANDER_STEP.y * height,
    WANDER_BOUNDS.yMin * height,
    WANDER_BOUNDS.yMax * height,
  ),
});

/** The entrance choreography (fill → burst → bloom) finishes around 2.4s. */
const WANDER_START_DELAY_MS = 2600;

/**
 * The OS setting or the app's own "Reduce motion" switch (Profile) — either
 * one stops the chase. It is read per gesture rather than subscribed to, so
 * flipping the switch takes hold on the next mousemove with nothing to
 * re-render; `handleLeave` returns the blob to its anchor without a ride if
 * the preference changed mid-chase.
 */
const prefersReducedMotion = motionIsReduced;

interface HeroBandProps {
  /** The band's own content. It supplies its own positioned wrapper. */
  children: ReactNode;
}

/**
 * The Home hero — the greeting band, and the one sanctioned gradient (XBG DS).
 *
 * The band's entrance (the two-pass fill, its finishing burst, and the glow's
 * bloom) lives in `src/index.css` under `.hero-band`; this component exists for
 * the one part CSS cannot do alone: the cyan glow chases the cursor while the
 * band is hovered, and drifts back to its corner when the cursor leaves.
 *
 * The chase is a rAF lerp rather than a CSS transition on the blob. A
 * transition restarts on every mousemove, so each frame's remaining travel is
 * re-eased from zero: the blob lurches toward each new sample and then holds,
 * which reads as snapping to the cursor and snapping home again. Interpolating
 * a *position* instead means the target can move mid-flight without the motion
 * restarting — the blob is always heading somewhere, at a speed set by how far
 * it has left to go, which is what makes both directions feel continuous.
 *
 * When nothing is hovering, the glow doesn't sit still and it doesn't go
 * "home": it WANDERS — drifting between random waypoints at a fraction of the
 * chase's pace, resuming from wherever the cursor left it. That means one rAF
 * stays alive while Home is on screen; it writes a single transform on a
 * decorative element per frame, and the browser suspends it entirely for
 * hidden tabs, which is a fair price for a band that reads as lit rather
 * than painted.
 *
 * Deliberately not React state. Pointer position is not something anything
 * renders from, and re-rendering Home per frame would re-render the work lists
 * with it. The loop writes one transform on one decorative element: no layout,
 * no paint of anything else.
 */
const HeroBand = ({ children }: HeroBandProps) => {
  const bandRef = useRef<HTMLDivElement>(null);
  const blobRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  /** Where the glow is, in px from the anchor. The lerp's only mutable state. */
  const currentRef = useRef({ x: 0, y: 0 });
  /** Where it is heading. Mousemove moves this; the loop is never restarted. */
  const targetRef = useRef({ x: 0, y: 0 });
  /** Chasing the cursor, or drifting between its own waypoints. */
  const modeRef = useRef<"chase" | "wander">("wander");

  const write = useCallback((x: number, y: number) => {
    const blob = blobRef.current;
    if (!blob) return;
    // The -50%/-50% half of the transform is the blob's own centring; the CSS
    // rule carries the same pair so the resting look survives with no inline
    // style at all (touch devices, reduced motion).
    blob.style.transform = `translate(-50%, -50%) translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0)`;
  }, []);

  const pickWaypoint = useCallback(() => {
    const band = bandRef.current;
    if (!band) return;
    const box = band.getBoundingClientRect();
    targetRef.current = wanderWaypoint(currentRef.current, box.width, box.height);
  }, []);

  const step = useCallback(() => {
    frameRef.current = null;
    const wandering = modeRef.current === "wander";
    const current = currentRef.current;
    const target = targetRef.current;
    const dx = target.x - current.x;
    const dy = target.y - current.y;

    if (wandering) {
      // Arrivals don't stop the drift — they just choose the next waypoint.
      if (Math.abs(dx) < WANDER_ARRIVE_PX && Math.abs(dy) < WANDER_ARRIVE_PX) pickWaypoint();
      current.x += (targetRef.current.x - current.x) * WANDER_EASE;
      current.y += (targetRef.current.y - current.y) * WANDER_EASE;
      write(current.x, current.y);
      frameRef.current = requestAnimationFrame(step);
      return;
    }

    const settled = Math.abs(dx) < GLOW_SETTLE_PX && Math.abs(dy) < GLOW_SETTLE_PX;
    if (settled) {
      current.x = target.x;
      current.y = target.y;
    } else {
      current.x += dx * GLOW_EASE;
      current.y += dy * GLOW_EASE;
    }
    write(current.x, current.y);
    // Caught up to a stationary cursor: stop, and let the next mousemove
    // (or the wander handed over on mouseleave) start a fresh loop.
    if (!settled) frameRef.current = requestAnimationFrame(step);
  }, [write, pickWaypoint]);

  const run = useCallback(() => {
    if (frameRef.current === null) frameRef.current = requestAnimationFrame(step);
  }, [step]);

  // The drift starts once the entrance choreography has finished, and never
  // for a reduced-motion session — there the glow simply rests at its anchor.
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const timer = setTimeout(() => {
      if (modeRef.current === "wander") {
        pickWaypoint();
        run();
      }
    }, WANDER_START_DELAY_MS);
    return () => clearTimeout(timer);
  }, [pickWaypoint, run]);

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  const handleMove = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const band = bandRef.current;
      if (!band || prefersReducedMotion()) return;
      const box = band.getBoundingClientRect();
      modeRef.current = "chase";
      // Only the target moves here — no writes, no measuring of the blob. A
      // mousemove firing several times between paints costs one assignment
      // each, and the loop reads whichever value was last.
      targetRef.current = {
        x: event.clientX - box.left - box.width * GLOW_ANCHOR_X,
        y: event.clientY - box.top,
      };
      run();
    },
    [run],
  );

  const handleLeave = useCallback(() => {
    // Reduced motion never tracked in the first place; just make sure the
    // resting look is restored if anything did move.
    if (prefersReducedMotion()) {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      currentRef.current = { x: 0, y: 0 };
      write(0, 0);
      return;
    }
    // No trip "home": the glow resumes wandering from wherever the cursor
    // left it — the same loop, a lazier ease, and its own next destination.
    modeRef.current = "wander";
    pickWaypoint();
    run();
  }, [run, write, pickWaypoint]);

  return (
    <div
      ref={bandRef}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      /* py-5 rather than py-6: Home is a one-screen page (see Dashboard) and
         the band is the largest single block of padding on it. */
      className="hero-band relative shrink-0 overflow-hidden rounded-2xl px-7 py-4 text-white"
    >
      {/* pass two of the fill: the brand gradient, riding in behind the flat
          navy edge. Decorative and never interactive. */}
      <div aria-hidden className="hero-band__flow" />
      <div aria-hidden className="hero-band__glow">
        <div ref={blobRef} className="hero-band__glow-blob" />
      </div>
      {children}
    </div>
  );
};

export default HeroBand;
